import { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@murm/ant";
import { CloseCircleFilled, SearchOutlined } from "@ant-design/icons";
import { useSegments } from "../../hooks/useSegments";
import { useTermIndex } from "../../hooks/useTermIndex";
import { useCommandPaletteMatches } from "../../hooks/useCommandPaletteMatches";
import { recordSelection, resolveRecentSelections } from "../../lib/recentSearches";
import { suggestComplementaryTerms } from "../../lib/search/matchTerms";
import { segmentsContainRealPill } from "../../lib/search/segmentsContainRealPill";
import TokenChip from "./TokenChip";
import CommandPalette from "./CommandPalette";
import BandSelector from "./BandSelector";
import HighlightedText from "./HighlightedText";
import { trackEvent } from "../../lib/analytics";
import { PENDO_ID, PENDO_EVENT, pendoId } from "../../lib/pendoIds";
import styles from "./TokenizedSearchInput.module.css";

// Continuous/Age open the band-selector (SELECTED) immediately on commit; Geography Level just
// adds the chip and refocuses, same as any plain term.
const needsBandConfig = (typeString) => typeString === "continuous" || typeString === "age";

// Age uses its own BandSelector template/labels (composeBandDisplay.js) -- "All Ages"/"Ages
// {bands}" and age-range buttons, not score percentiles.
const AGE_BANDS = ["18-24", "25-34", "35-44", "45-54", "55-64", "65-74", "75+"];
const AGE_BAND_SELECTOR_PROPS = {
  bands: AGE_BANDS,
  allLabel: "All Ages",
  allDisplay: "All Ages",
  bandedPrefix: "Ages",
};

// Figma: max 5 ADD TO YOUR SEARCH rows -- separate cap from DEFAULT_RELATED_CAP (4, typed
// RELATED MATCHES).
const MAX_ADD_TO_SEARCH = 5;

const MIN_SUBMIT_CHARACTERS = 3;

const TokenizedSearchInput = ({
  onSubmit,
  onOpenCatalog,
  onClear,
  placeholder = "Search",
  richPlaceholderSegments = null,
  initialQuery = null,
  initialSegments = null,
  onInitialSegmentsResolved,
}) => {
  const { termIndex, isLoading: isLoadingTermIndex } = useTermIndex();
  const {
    segments,
    activeSegmentId,
    activeText,
    textBeforeCursor,
    activeFragment,
    activePlaceFragment,
    previousToken,
    setActiveFragment,
    commitToken,
    updateToken,
    removeToken,
    activateSegment,
    clear,
    resetToText,
    resetToSegments,
    flattenedText,
    templateSegments,
  } = useSegments();

  const [configuringTokenId, setConfiguringTokenId] = useState(null);
  const configuringToken =
    segments.find((s) => s.type === "token" && s.id === configuringTokenId) ?? null;
  const [isInputFocused, setIsInputFocused] = useState(false);
  // Overrides isPaletteOpen: a typed fragment keeps the palette open regardless of focus, so
  // blurring alone can't close it. Set by an outside click, cleared by the next interaction.
  const [isDismissed, setIsDismissed] = useState(false);
  // ADD TO YOUR SEARCH content once no config panel is open to source it from -- persists after
  // Escape or a commit that never opens SELECTED. Cleared alongside `configuringTokenId`.
  const [lastConfiguredSiblings, setLastConfiguredSiblings] = useState([]);

  const paletteRef = useRef(null);
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  // Set by handleCommitTerm, read by the effect below -- see that effect for why a same-tick
  // inputRef.current?.focus() call there can't work.
  const shouldRefocusAfterCommitRef = useRef(false);

  // Committing/activating a segment mints a new `segment.id`, remounting the <input> -- a
  // synchronous focus() at commit time would hit the node being torn down, so this defers to an
  // effect that runs after the new one mounts.
  useEffect(() => {
    if (shouldRefocusAfterCommitRef.current) {
      shouldRefocusAfterCommitRef.current = false;
      inputRef.current?.focus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only cares about the active
    // segment's identity actually changing (i.e. a fresh input node mounting), not every re-render.
  }, [activeSegmentId]);

  // mousedown, not click, so this runs before the palette's own onMouseDown-preventDefault
  // (below) would otherwise keep the input focused.
  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsDismissed(true);
        setConfiguringTokenId(null);
        setLastConfiguredSiblings([]);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  // Seeds the bar from a Data Catalog selection (Redux `state.search.query`, see SearchBar.js).
  // `setIsDismissed(true)`: otherwise isPaletteOpen treats the seeded, non-empty activeText as
  // reason to show the palette on its own.
  useEffect(() => {
    if (initialQuery) {
      resetToText(initialQuery);
      setIsDismissed(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- resetToText is stable (useCallback, no deps)
  }, [initialQuery]);

  // Seeds the bar from a template click's raw segments (Redux `state.search.pendingSegments`),
  // resolving each pill against the live term index into an editable token.
  //
  // Waits for the term index's first load -- resolving too early would mark every pill
  // "unresolved" even though the real match arrives moments later. `resolvedSegmentsRef` stops
  // this from re-firing for the same click once resolved.
  const resolvedSegmentsRef = useRef(null);
  useEffect(() => {
    if (!initialSegments || isLoadingTermIndex) return;
    if (initialSegments === resolvedSegmentsRef.current) return;
    resolvedSegmentsRef.current = initialSegments;
    resetToSegments(initialSegments, termIndex);
    // Figma: input focuses once a template's segments land -- resetToSegments remounts the input
    // (new key), so this defers to the activeSegmentId effect above instead of focusing here.
    shouldRefocusAfterCommitRef.current = true;
    // Notifies the caller only after resolution actually happens, not the instant initialSegments
    // arrives, so the one-shot prop isn't cleared before it can resolve.
    onInitialSegmentsResolved?.();
  }, [initialSegments, isLoadingTermIndex, termIndex, resetToSegments, onInitialSegmentsResolved]);

  const committed = useMemo(
    () =>
      segments
        .filter((s) => s.type === "token")
        .map((s) => ({ id: s.termId, sourceType: s.sourceType })),
    [segments]
  );
  const matches = useCommandPaletteMatches(
    configuringToken ? "" : activeFragment,
    configuringToken ? "" : activePlaceFragment,
    { interpretText: configuringToken ? "" : textBeforeCursor, committed }
  );
  // Populates ADD TO YOUR SEARCH from the token's own siblings. Falls back, in order, for types
  // with none (places, Age, an unresolved pill): complementary-category suggestions, recent
  // selections, then a plain slice of the term index.
  const resolveAddToSearchRows = (ownSiblings, extraUsedSourceType) => {
    const own = (ownSiblings ?? []).slice(0, MAX_ADD_TO_SEARCH);
    if (own.length > 0) return own;

    const usedSourceTypes = new Set(
      segments.filter((s) => s.type === "token").map((s) => s.sourceType)
    );
    if (extraUsedSourceType) usedSourceTypes.add(extraUsedSourceType);
    const complementary = suggestComplementaryTerms(termIndex, usedSourceTypes, MAX_ADD_TO_SEARCH);
    if (complementary.length > 0) return complementary;

    const recent = resolveRecentSelections(termIndex);
    if (recent.length > 0) return recent;

    return termIndex.slice(0, MAX_ADD_TO_SEARCH);
  };

  const addToSearchMatches = configuringToken
    ? { exactMatches: [], relatedMatches: resolveAddToSearchRows(configuringToken.siblings) }
    : lastConfiguredSiblings.length > 0
      ? { exactMatches: [], relatedMatches: lastConfiguredSiblings }
      : matches;

  const isEmpty = segments.length === 1 && activeText === "";

  // The 0-character, returning-user state. Scoped to `isEmpty` (the whole bar), not just an empty
  // fragment -- that would also resurface this right after committing a token mid-query.
  const recentSelections = isEmpty ? resolveRecentSelections(termIndex) : [];

  const isPaletteOpen =
    !isDismissed &&
    (Boolean(activeText) ||
      Boolean(configuringToken) ||
      (isInputFocused && (recentSelections.length > 0 || lastConfiguredSiblings.length > 0)));

  const canSubmit =
    segmentsContainRealPill(templateSegments) ||
    flattenedText.trim().length >= MIN_SUBMIT_CHARACTERS;

  const runSubmit = () => {
    if (!canSubmit) return;
    matches.cancelPlaceMatches();
    trackEvent(PENDO_EVENT.SEARCH_SUBMITTED, {
      tokenCount: segments.filter((segment) => segment.type === "token").length,
    });
    onSubmit?.(flattenedText, templateSegments);
  };

  // A term from ADD TO YOUR SEARCH always adds a new token rather than replacing the one being
  // configured. `configuringTokenId` only changes if the new term itself needs a config step.
  const handleCommitTerm = (term) => {
    recordSelection({ display: term.display, category: term.category });
    trackEvent(PENDO_EVENT.QUERY_ITEM_ADDED, { tokenType: term.sourceType });
    const id = commitToken(term);
    // Unconditional: Geography Level never opens SELECTED but should still show siblings
    // immediately. `term.sourceType` is passed explicitly since `segments` hasn't updated yet.
    setLastConfiguredSiblings(resolveAddToSearchRows(term.siblings, term.sourceType));
    if (needsBandConfig(term.feature?.type)) {
      setConfiguringTokenId(id);
    }
    shouldRefocusAfterCommitRef.current = true;
  };

  // Persists the full selection, not just the composed display text -- otherwise reopening
  // config loses track of which bands/aggregation were active and restarts at the default.
  const handleBandChange = (update) => {
    if (configuringTokenId) {
      updateToken(configuringTokenId, {
        display: update.display,
        bandSelection: {
          isAll: update.isAll,
          selectedPresets: update.selectedPresets,
          customRange: update.customRange,
          aggregation: update.aggregation,
        },
      });
    }
  };

  const handleRemoveSelected = () => {
    trackEvent(PENDO_EVENT.QUERY_ITEM_REMOVED, {
      tokenType: configuringToken?.sourceType,
      method: "palette",
    });
    if (configuringTokenId) removeToken(configuringTokenId);
    setConfiguringTokenId(null);
    setLastConfiguredSiblings([]);
  };

  const handleKeyDown = (event) => {
    // Two-stage: while configuring, Escape steps back to ADD-TO-YOUR-SEARCH first; otherwise (or
    // a second Escape) it dismisses the whole palette.
    if (event.key === "Escape") {
      if (configuringToken) {
        setConfiguringTokenId(null);
      } else {
        setIsDismissed(true);
        setLastConfiguredSiblings([]);
      }
      return;
    }
    if (event.key === "Backspace" && !configuringToken && activeText === "" && previousToken) {
      event.preventDefault();
      // removeToken remounts the input (new id, like any commit), so it needs the same deferred
      // refocus.
      shouldRefocusAfterCommitRef.current = true;
      trackEvent(PENDO_EVENT.QUERY_ITEM_REMOVED, {
        tokenType: previousToken.sourceType,
        method: "backspace",
      });
      removeToken(previousToken.id);
      return;
    }
    if (event.key === "Enter" && !isPaletteOpen) {
      runSubmit();
      return;
    }
    paletteRef.current?.handleKeyDown(event);
  };

  const openTokenConfig = (segment) => {
    setIsDismissed(false);
    setConfiguringTokenId(segment.id);
    setLastConfiguredSiblings(resolveAddToSearchRows(segment.siblings));
    // Clicking a chip steals focus onto it (TokenChip.js is `tabIndex={0}`); a direct focus() is
    // enough here since this doesn't remount the input, unlike a fresh commit.
    inputRef.current?.focus();
  };

  const showRichPlaceholder = Boolean(richPlaceholderSegments) && isEmpty;

  return (
    <div ref={containerRef} className={styles.root}>
      {/* Buttons live in this OUTER, non-wrapping row -- inside the wrapping row below, a
          trailing input on its own line (flex:1) would stretch and shove them far right. Outer
          placement also keeps them visible when the inner content scrolls. */}
      <div className={styles.bar}>
        <div
          data-testid="search-tokens-row"
          // Figma height spec (80px min, 104px max, then scrolls) -- pinned in this file's own
          // "search bar container height" test.
          className={styles.tokens}
        >
          {showRichPlaceholder && (
            // Scoped to this row (shrunk by the buttons' width above), not the whole bar -- a
            // full-bar overlay let the trailing text run behind the submit button. Wraps like the
            // real segments below instead of truncating.
            <div className={styles.placeholder} aria-hidden="true">
              <HighlightedText segments={richPlaceholderSegments} variant="placeholder" />
            </div>
          )}
          {segments.map((segment) => {
            const isActive = segment.id === activeSegmentId;
            // Only the trailing input gets flex: 1 -- a mid-string "keep typing here" segment can
            // have real suffix text after it, which flex: 1 would shove away.
            //
            // Non-trailing needs flex-shrink: 0 ("0 0 auto"), or the row squeezes the input below
            // its `size`-driven width and the native <input> scrolls its text internally, hiding
            // content (real-browser-only; jsdom doesn't compute flex layout).
            const isTrailingSegment = segment === segments[segments.length - 1];

            if (segment.type === "token") {
              return (
                <TokenChip
                  key={segment.id}
                  display={segment.display}
                  tokenType={segment.sourceType}
                  selected={segment.id === configuringTokenId}
                  onOpenConfig={() => openTokenConfig(segment)}
                />
              );
            }

            if (isActive) {
              // Native <input> on purpose (XPLRR-106 exception): segment editing relies on the raw
              // element's selectionStart, `size`-driven width and ref; Ant's Input wraps it.
              return (
                <input
                  key={segment.id}
                  ref={inputRef}
                  value={segment.value}
                  placeholder={
                    segments.length === 1 && !richPlaceholderSegments ? placeholder : undefined
                  }
                  onChange={(e) => {
                    setIsDismissed(false);
                    // Typing exits configuring mode, same as Escape/an outside click -- otherwise
                    // it stays stuck showing the token's SELECTED panel instead of matching what's
                    // now being typed.
                    if (configuringTokenId) setConfiguringTokenId(null);
                    setLastConfiguredSiblings([]);
                    // Passes the real cursor position, not just the value -- mid-string editing can
                    // have content on both sides of it (see useSegments.js).
                    setActiveFragment(e.target.value, e.target.selectionStart);
                  }}
                  onKeyDown={handleKeyDown}
                  {...pendoId(PENDO_ID.SEARCH_INPUT)}
                  onFocus={() => {
                    setIsDismissed(false);
                    setIsInputFocused(true);
                  }}
                  onBlur={() => setIsInputFocused(false)}
                  aria-label="Search"
                  // Only meaningfully affects the non-trailing case (see above) -- `flex: 1`'s own
                  // 0% basis already overrides whatever `size` would otherwise imply.
                  size={Math.max(segment.value.length, 1)}
                  className={`${styles.input} ${
                    isTrailingSegment ? styles.inputTrailing : styles.inputInline
                  }`}
                />
              );
            }

            // Every plain-text span is a real cursor position -- click activates it here
            // (useSegments.js). minWidth keeps an empty gap (e.g. after a removed token) clickable.
            const activateThisSegment = () => {
              setIsDismissed(false);
              if (configuringTokenId) setConfiguringTokenId(null);
              setLastConfiguredSiblings([]);
              shouldRefocusAfterCommitRef.current = true;
              activateSegment(segment.id);
            };

            // Display-only trim: `segment.value` keeps real whitespace (useSegments.js's
            // mashing-prevention depends on it), but an inactive span doesn't need to show it.
            const displayValue = segment.value.trim();

            return (
              <span
                key={segment.id}
                role="textbox"
                tabIndex={0}
                onClick={activateThisSegment}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    activateThisSegment();
                  }
                }}
                className={
                  displayValue ? styles.segment : `${styles.segment} ${styles.segmentEmpty}`
                }
              >
                {displayValue}
              </span>
            );
          })}
        </div>

        {/* 40x40 per Figma (Button/Global/controlHeight). CloseCircleFilled's own fill draws the
          circle, so no separate background is needed. */}
        {!isEmpty && (
          <Button
            {...pendoId(PENDO_ID.SEARCH_CLEAR)}
            type="text"
            shape="circle"
            className={styles.clear}
            icon={<CloseCircleFilled className={styles.clearIcon} />}
            onClick={() => {
              clear();
              setLastConfiguredSiblings([]);
              // Notifies SearchBar.js to reset its own copy of the selection (Redux `parameters`),
              // which would otherwise linger after the bar looks empty.
              onClear?.();
            }}
            aria-label="Clear search"
          />
        )}
        {/* Same 40x40 base as the clear button above; antd's own `type="primary"` colors the
          icon automatically, so only the default state's icon needs an explicit gray. */}
        <Button
          {...pendoId(PENDO_ID.SEARCH_SUBMIT)}
          type={isEmpty ? "default" : "primary"}
          className={styles.submit}
          icon={
            <SearchOutlined
              className={
                isEmpty ? `${styles.submitIcon} ${styles.submitIconMuted}` : styles.submitIcon
              }
            />
          }
          disabled={!canSubmit}
          onClick={runSubmit}
          aria-label="Submit search"
        />
      </div>

      {isPaletteOpen && (
        // preventDefault: clicking a row would blur the input first, flipping isPaletteOpen
        // false and unmounting the row before its own click fires.
        <div
          onMouseDown={(e) => e.preventDefault()}
          data-testid="command-palette-popover"
          className={styles.palette}
        >
          <CommandPalette
            ref={paletteRef}
            fragment={activeFragment}
            headerText={configuringToken ? null : matches.phrase}
            // Always the whole query, not just the trailing fragment -- `activeText` alone
            // excludes anything already tokenized before it.
            literalText={flattenedText}
            mode={configuringToken ? null : matches.mode}
            exactMatches={addToSearchMatches.exactMatches}
            relatedMatches={addToSearchMatches.relatedMatches}
            recentSelections={recentSelections}
            placeStatus={configuringToken ? "idle" : matches.placeStatus}
            onRetryPlaces={matches.retryPlaceMatches}
            onCommitTerm={handleCommitTerm}
            onRunLiteral={runSubmit}
            onOpenCatalog={onOpenCatalog}
            selectedTerm={configuringToken}
            onRemoveSelected={handleRemoveSelected}
            configPanel={
              configuringToken && needsBandConfig(configuringToken.payload?.type) ? (
                <BandSelector
                  tokenType={configuringToken.payload?.type}
                  metricDisplay={configuringToken.originalDisplay}
                  onChange={handleBandChange}
                  initialSelection={configuringToken.bandSelection}
                  // A continuous metric's own scale (e.g. [0, 1] for a modeled probability) --
                  // null for an ordinary 0-100 score, which BandSelector already defaults to.
                  // Age ignores this; its own fixed band labels come from AGE_BAND_SELECTOR_PROPS.
                  range={configuringToken.payload?.range}
                  // The backend leaks a default `available_aggregations` onto Age's payload;
                  // excluded here since Age should never show the toggle.
                  availableAggregations={
                    configuringToken.payload?.type === "age"
                      ? []
                      : (configuringToken.payload?.available_aggregations ?? [])
                  }
                  {...(configuringToken.payload?.type === "age" ? AGE_BAND_SELECTOR_PROPS : {})}
                />
              ) : null
            }
          />
        </div>
      )}
    </div>
  );
};

export default TokenizedSearchInput;
